/**
 * Gemeinsamer Frage-Composer (Vereinfachung S5, 2026-07-08).
 *
 * Ein Feld + Senden — der 95 %-Fall sieht vor dem Tippen nur Feld, Stufen-Chip
 * und Senden. Die Denk-Stufen (Schnell/Standard/Gründlich/Experte) liegen hinter
 * einem ⚙-Popover; die Auswahl wird komponenten-übergreifend in localStorage
 * `sb_ask_level` gemerkt (Default „Standard"). Der Hilfetext „↵ senden · ⇧+↵ neue
 * Zeile" ist nur noch title-Tooltip des Feldes, kein sichtbarer Text.
 *
 * Wird von Chat.tsx, ProjectChatView.tsx und DatasetChatView.tsx genutzt — alle
 * drei Composer sind damit identisch aufgebaut. Das Modell-Routing bleibt beim
 * jeweiligen Kontext (er liest `level` und leitet daraus sein Modell ab).
 */
import { FormEvent, useEffect, useState } from "react";
import { Send, Settings2, Paperclip, Loader2 } from "lucide-react";
import { Button } from "../ui";
import {
  AttachInput,
  AttachmentChip,
  type DocAttachmentApi,
} from "./useDocAttachment";
import ResearchModePicker, { type ResearchMode } from "./ResearchModePicker";

export type AskLevel = "easy" | "medium" | "hard" | "sophisticated";

export const ASK_LEVEL_KEY = "sb_ask_level";

export const ASK_LEVELS: { id: AskLevel; label: string; hint: string }[] = [
  { id: "easy",          label: "Schnell",   hint: "Schnelle Antwort für einfache Fragen" },
  { id: "medium",        label: "Standard",  hint: "Ausgewogen für die meisten Fragen" },
  { id: "hard",          label: "Gründlich", hint: "Tiefere Analyse, etwas langsamer" },
  { id: "sophisticated", label: "Experte",   hint: "Höchste Sorgfalt für komplexe Fragen" },
];

/** Level-State mit localStorage-Persistenz (`sb_ask_level`), Default „Standard". */
export function useAskLevel(): [AskLevel, (l: AskLevel) => void] {
  const [level, setLevel] = useState<AskLevel>(() => {
    const v = localStorage.getItem(ASK_LEVEL_KEY) as AskLevel | null;
    return v && ASK_LEVELS.some((l) => l.id === v) ? v : "medium";
  });
  useEffect(() => {
    localStorage.setItem(ASK_LEVEL_KEY, level);
  }, [level]);
  return [level, setLevel];
}

export interface AskComposerProps {
  q: string;
  setQ: (v: string) => void;
  onSend: (e?: FormEvent) => void;
  busy: boolean;
  composerRef: React.RefObject<HTMLTextAreaElement>;
  level: AskLevel;
  setLevel: (l: AskLevel) => void;
  placeholder?: string;
  /** Optional: aktiviert 📎 + Drag&Drop + Anhang-Chip. Ohne diese Prop
   *  verhält sich der Composer exakt wie bisher (Project/Dataset-Chat). */
  attach?: DocAttachmentApi;
  /** Optional: Recherche-Tiefe-Picker (off/normal/deep). Ohne diese Props wird
   *  kein Picker gezeigt (z. B. Dataset-Chat, wo Perplexity irrelevant ist). */
  researchMode?: ResearchMode;
  setResearchMode?: (m: ResearchMode) => void;
}

export default function AskComposer({
  q,
  setQ,
  onSend,
  busy,
  composerRef,
  level,
  setLevel,
  placeholder = "Frage stellen …",
  attach,
  researchMode,
  setResearchMode,
}: AskComposerProps) {
  // EIN Optionen-Popover bündelt alle Sekundär-Controls (Denk-Stufe,
  // Recherche-Tiefe, Anhang) — Progressive Disclosure (I-06): im Ausgangs-
  // zustand (kein Text, kein Fokus) ist nur Feld + Senden sichtbar, der
  // ⚙-Trigger erscheint erst bei Fokus/Text. So bleiben ≤2 Bedienelemente.
  const [optsOpen, setOptsOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const currentLabel =
    ASK_LEVELS.find((l) => l.id === level)?.label ?? "Standard";
  // Trigger einblenden sobald der Nutzer den Composer benutzt (Fokus, Text
  // oder offenes Popover). Idle = keins davon → Trigger verborgen.
  const showTrigger = focused || q.trim().length > 0 || optsOpen;
  const hasResearch = researchMode !== undefined && !!setResearchMode;

  const onDrop = (e: React.DragEvent) => {
    if (!attach) return;
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) attach.onFile(f);
  };

  return (
    <form onSubmit={onSend}>
      <div
        onDragOver={
          attach
            ? (e) => {
                e.preventDefault();
                setDragOver(true);
              }
            : undefined
        }
        onDragLeave={
          attach
            ? (e) => {
                if (!e.currentTarget.contains(e.relatedTarget as Node))
                  setDragOver(false);
              }
            : undefined
        }
        onDrop={attach ? onDrop : undefined}
        className={
          "bg-surface border rounded-2xl px-4 py-3 shadow-sm hover:shadow-md focus-within:shadow-md transition " +
          (dragOver
            ? "border-accent-300 bg-accent-50 dark:bg-accent-900/10"
            : "border-border focus-within:border-accent-300")
        }
      >
        {attach && <AttachInput api={attach} />}
        <textarea
          ref={composerRef}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={placeholder}
          aria-label="Chat-Eingabe"
          title="↵ senden · ⇧+↵ neue Zeile"
          rows={2}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              onSend();
            }
          }}
          className="w-full bg-transparent focus:outline-none resize-none text-[15px] placeholder:text-text-3"
        />
        {attach?.attachment && (
          <div className="mt-2">
            <AttachmentChip attachment={attach.attachment} onRemove={attach.clear} />
          </div>
        )}
        <div className="flex items-center justify-between mt-2 gap-2">
         <div className="flex items-center gap-1 min-w-0">
          {/* ⚙ Optionen — EIN Trigger für alle Sekundär-Controls (Denk-Stufe,
              Recherche-Tiefe, Anhang). Erscheint erst bei Fokus/Text, damit im
              Ausgangszustand nur Feld + Senden sichtbar sind (I-06). */}
          {showTrigger && (
          <div className="relative">
            <button
              type="button"
              onClick={() => setOptsOpen((o) => !o)}
              className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs text-text-2 hover:bg-surface-3 transition-colors"
              title="Optionen — Antwort-Tiefe, Recherche, Anhang"
              aria-haspopup="menu"
              aria-expanded={optsOpen}
            >
              <Settings2 className="w-3.5 h-3.5 text-text-3" />
              <span className="truncate">{currentLabel}</span>
            </button>
            {optsOpen && (
              <>
                <button
                  type="button"
                  aria-label="Optionen schließen"
                  className="fixed inset-0 z-30 cursor-default"
                  onClick={() => setOptsOpen(false)}
                />
                <div
                  role="menu"
                  className="absolute z-40 bottom-full mb-1.5 left-0 w-72 max-w-[calc(100vw-2rem)] bg-surface border border-border rounded-xl shadow-lg py-1"
                >
                  {/* Denk-Stufe (4 Radio-Optionen), gemerkt in localStorage. */}
                  <div className="px-3 pt-1.5 pb-1 text-[11px] font-medium text-text-3 uppercase tracking-wide">
                    Antwort-Tiefe
                  </div>
                  {ASK_LEVELS.map((l) => {
                    const active = level === l.id;
                    return (
                      <button
                        key={l.id}
                        type="button"
                        role="menuitemradio"
                        aria-checked={active}
                        onClick={() => setLevel(l.id)}
                        className="w-full flex items-start gap-2.5 px-3 py-2 text-left hover:bg-surface-2 transition-colors"
                      >
                        <span
                          className={
                            active
                              ? "mt-0.5 w-4 h-4 rounded-full border-2 border-accent-500 grid place-items-center shrink-0"
                              : "mt-0.5 w-4 h-4 rounded-full border-2 border-border-strong shrink-0"
                          }
                        >
                          {active && (
                            <span className="w-1.5 h-1.5 rounded-full bg-accent-500" />
                          )}
                        </span>
                        <span className="flex-1 min-w-0">
                          <span className="block text-sm text-text font-medium">
                            {l.label}
                          </span>
                          <span className="block text-xs text-text-3 leading-snug">
                            {l.hint}
                          </span>
                        </span>
                      </button>
                    );
                  })}
                  {/* Recherche-Tiefe (off/normal/deep) — nur wo aktiviert. */}
                  {hasResearch && (
                    <>
                      <div className="mt-1 mx-3 border-t border-border" />
                      <div className="px-3 pt-2">
                        <ResearchModePicker
                          mode={researchMode!}
                          setMode={setResearchMode!}
                          variant="full"
                        />
                      </div>
                    </>
                  )}
                  {/* 📎 Dokument anhängen (nur wenn attach aktiviert). */}
                  {attach && (
                    <>
                      <div className="mt-2 mx-3 border-t border-border" />
                      <button
                        type="button"
                        onClick={() => {
                          setOptsOpen(false);
                          attach.openDialog();
                        }}
                        className="w-full flex items-center gap-2.5 px-3 py-2 text-left text-sm text-text hover:bg-surface-2 transition-colors"
                      >
                        {attach.uploading ? (
                          <Loader2 className="w-4 h-4 animate-spin text-accent-500 shrink-0" />
                        ) : (
                          <Paperclip className="w-4 h-4 text-text-3 shrink-0" />
                        )}
                        <span>Dokument anhängen</span>
                        <span className="ml-auto text-xs text-text-3">
                          PDF · MD · Text
                        </span>
                      </button>
                    </>
                  )}
                </div>
              </>
            )}
          </div>
          )}
         </div>

          <Button
            type="submit"
            variant="primary"
            loading={busy}
            disabled={!q.trim()}
            icon={<Send className="w-4 h-4" />}
          >
            Senden
          </Button>
        </div>
      </div>
    </form>
  );
}
